import type { SubscriptionStatus } from "@/contexts/AuthContext";

export type TierId = "tier_1" | "tier_2" | "tier_3" | "tier_4" | "tier_5";
export type PriceIdKey =
  | "starter"
  | "bronze_monthly"
  | "bronze_annual"
  | "silver_monthly"
  | "silver_annual"
  | "gold_monthly"
  | "gold_annual";

export type PlanFlowMode = "activate" | "reinstate" | "upgrade" | "current" | "contact";

export interface PlanFlowDecision {
  mode: PlanFlowMode;
  targetTierId: TierId | null;
  targetPriceIdKey: PriceIdKey | null;
}

const TIER_RANK: Record<TierId, number> = {
  tier_1: 1,
  tier_2: 2,
  tier_3: 3,
  tier_4: 4,
  tier_5: 5,
};

const TIER_TO_MONTHLY_PRICE_KEY: Record<Exclude<TierId, "tier_5">, PriceIdKey> = {
  tier_1: "starter",
  tier_2: "bronze_monthly",
  tier_3: "silver_monthly",
  tier_4: "gold_monthly",
};

const TIER_TO_ANNUAL_PRICE_KEY: Record<Exclude<TierId, "tier_1" | "tier_5">, PriceIdKey> = {
  tier_2: "bronze_annual",
  tier_3: "silver_annual",
  tier_4: "gold_annual",
};

const PRICE_KEY_TO_TIER_ID: Partial<Record<PriceIdKey, TierId>> = {
  starter: "tier_1",
  bronze_monthly: "tier_2",
  bronze_annual: "tier_2",
  silver_monthly: "tier_3",
  silver_annual: "tier_3",
  gold_monthly: "tier_4",
  gold_annual: "tier_4",
};

const TIER_NAME_TO_ID: Record<string, TierId> = {
  starter: "tier_1",
  bronze: "tier_2",
  silver: "tier_3",
  gold: "tier_4",
  enterprise: "tier_5",
};

export function getTierRank(tierId: string | null | undefined): number {
  if (!tierId || !(tierId in TIER_RANK)) return 0;
  return TIER_RANK[tierId as TierId];
}

export function getTierIdForPriceKey(priceIdKey: string | null | undefined): TierId | null {
  if (!priceIdKey) return null;
  return PRICE_KEY_TO_TIER_ID[priceIdKey as PriceIdKey] || null;
}

export function getTierIdForTierName(tierName: string | null | undefined): TierId | null {
  if (!tierName) return null;
  return TIER_NAME_TO_ID[tierName.toLowerCase()] || null;
}

export function getTierIdFromSubscription(subscription: SubscriptionStatus | null | undefined): TierId | null {
  return getTierIdForPriceKey(subscription?.active_price_id_key)
    || getTierIdForTierName(subscription?.subscription_tier)
    || null;
}

export function resolveTargetPriceIdKey(
  tierId: TierId | null | undefined,
  currentPriceIdKey?: string | null,
): PriceIdKey | null {
  if (!tierId) return null;
  if (tierId === "tier_5") return null;
  if (tierId === "tier_1") return "starter";

  const prefersAnnual = currentPriceIdKey?.includes("annual");
  if (prefersAnnual) {
    return TIER_TO_ANNUAL_PRICE_KEY[tierId as Exclude<TierId, "tier_1" | "tier_5">];
  }

  return TIER_TO_MONTHLY_PRICE_KEY[tierId as Exclude<TierId, "tier_5">];
}

export function determinePlanFlow({
  currentTierId,
  matchedTierId,
  currentPriceIdKey,
  subscribed,
  billingHasHistory,
  billingAccessActive,
}: {
  currentTierId: TierId | null;
  matchedTierId: TierId | null;
  currentPriceIdKey?: string | null;
  subscribed?: boolean;
  billingHasHistory?: boolean;
  billingAccessActive?: boolean;
}): PlanFlowDecision {
  if (!matchedTierId) {
    return { mode: "current", targetTierId: null, targetPriceIdKey: null };
  }

  if (matchedTierId === "tier_5") {
    return {
      mode: "contact",
      targetTierId: matchedTierId,
      targetPriceIdKey: null,
    };
  }

  const targetPriceIdKey = resolveTargetPriceIdKey(matchedTierId, currentPriceIdKey);

  if (billingHasHistory && billingAccessActive === false) {
    return {
      mode: "reinstate",
      targetTierId: matchedTierId,
      targetPriceIdKey,
    };
  }

  if (!billingHasHistory && !subscribed) {
    return {
      mode: "activate",
      targetTierId: matchedTierId,
      targetPriceIdKey,
    };
  }

  if (getTierRank(matchedTierId) > getTierRank(currentTierId)) {
    return {
      mode: "upgrade",
      targetTierId: matchedTierId,
      targetPriceIdKey,
    };
  }

  return {
    mode: "current",
    targetTierId: matchedTierId,
    targetPriceIdKey,
  };
}
